import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "../sass/landing/plans.scss";

const STRIPE_PRICE_ESSENCIAL =
  process.env.REACT_APP_STRIPE_PRICE_ESSENCIAL || "price_1U18NBKKOfm7LsUJzf5rztCc";
const STRIPE_PRICE_PREMIUM =
  process.env.REACT_APP_STRIPE_PRICE_PREMIUM || "price_1U18OVKKOfm7LsUJnbIcjwgc";

const PLANS = [
  {
    id: "essencial",
    name: "Essencial",
    fullName: "PsyAssist Essencial",
    eyebrow: "Para colocar a rotina em ordem",
    price: "R$ 29,97",
    pricePayload: "R$ 29,97/MÊS",
    normalPrice: "R$ 49,97",
    normalPricePayload: "R$ 49,97/MÊS",
    priceId: STRIPE_PRICE_ESSENCIAL,
    description: "Tudo o que você precisa para manter pacientes, agenda, prontuários e financeiro organizados no mesmo lugar.",
    highlight: false,
    promotionalOffer: true,
    promotionalLabel: "Preço promocional de lançamento",
    features: [
      "Cadastro e organização de pacientes",
      "Prontuário e histórico de evoluções",
      "Anamnese clínica",
      "Agenda e agendamentos recorrentes",
      "Controle de sessões e pagamentos",
      "Receitas, despesas e relatórios",
      "Gestão de leads",
      "Controle de pagamentos pendentes",
      "Organização administrativa da rotina",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    fullName: "PsyAssist Premium",
    eyebrow: "Para tirar tarefas repetitivas das suas mãos",
    price: "R$ 69,97",
    pricePayload: "R$ 69,97/MÊS",
    normalPrice: "R$ 99,97",
    normalPricePayload: "R$ 99,97/MÊS",
    priceId: STRIPE_PRICE_PREMIUM,
    description: "Além de organizar, o PsyAssist passa a acompanhar confirmações, cobranças, comunicações e integrações com você.",
    highlight: true,
    promotionalOffer: true,
    promotionalLabel: "Preço promocional de lançamento",
    features: [
      "Tudo do plano Essencial",
      "Lembretes automáticos de sessão",
      "Confirmações pelo WhatsApp",
      "Régua automática de cobrança",
      "Cobranças por WhatsApp e e-mail",
      "Pix incluído nas comunicações",
      "Central de Comunicações",
      "Google Agenda sincronizado",
      "Google Meet para sessões online",
      "Dashboard e indicadores avançados",
      "PSYA com contexto da sua operação",
      "Insights financeiros e operacionais",
      "Suporte prioritário",
    ],
  },
];

const SELECTED_PLAN_STORAGE_KEY = "psyassist:selected-plan";
const PLAN_COPY = {
  essencial: {
    idea: "Para organizar e centralizar.",
    summary: "Pacientes, agenda, prontuário, anamnese, sessões e financeiro no mesmo lugar. Para tirar a rotina do papel, das planilhas e das conversas soltas.",
  },
  premium: {
    idea: "Para organizar e automatizar.",
    summary: "Tudo do Essencial e mais: lembretes, confirmações e cobranças enviados por WhatsApp e e-mail, Google Agenda e Meet conectados e a PSYA.",
  },
};

const PlanColumn = ({ plan, onSelect }) => {
  const copy = PLAN_COPY[plan.id] || {};
  return (
    <article className={`lp-plan ${plan.highlight ? "lp-plan--premium" : ""}`} aria-labelledby={`plan-${plan.id}`}>
      <header className="lp-plan__head">
        <h3 id={`plan-${plan.id}`}>{plan.name}</h3>
        <p className="lp-plan__idea">{copy.idea}</p>
        <p>{copy.summary || plan.description}</p>
      </header>

      <div className="lp-plan__price">
        <p className="lp-plan__amount"><strong>{plan.price}</strong><span>/mês</span></p>
        <p className="lp-plan__normal">Condição de lançamento. Depois, {plan.normalPrice}/mês.</p>
      </div>

      <button
        type="button"
        className={`lp-button ${plan.highlight ? "lp-button--light" : "lp-button--primary"} lp-plan__cta`}
        onClick={() => onSelect(plan)}
        aria-label={`Começar 7 dias grátis no plano ${plan.fullName}`}
      >
        Começar meus 7 dias grátis
        <ArrowRight size={17} aria-hidden="true" />
      </button>

      <ul className="lp-plan__features">
        {plan.features.map((feature) => <li key={feature}>{feature}</li>)}
      </ul>
    </article>
  );
};

const Plan = () => {
  const navigate = useNavigate();
  const selectPlan = (plan) => {
    if (!plan?.id || !plan?.priceId) {
      console.error("Plano inválido ou sem priceId.", plan);
      return;
    }

    const payload = {
      planId: plan.id,
      planName: plan.fullName,
      planPriceIndividual: plan.pricePayload,
      planPriceClinica: plan.pricePayload,
      planNormalPriceIndividual: plan.normalPricePayload,
      planNormalPriceClinica: plan.normalPricePayload,
      priceId: plan.priceId,
      tipoUsuario: "individual",
      promotionalOffer: plan.promotionalOffer === true,
      promotionalLabel: plan.promotionalLabel,
      trialDays: 7,
      selectedAt: new Date().toISOString(),
    };

    try {
      sessionStorage.setItem(SELECTED_PLAN_STORAGE_KEY, JSON.stringify(payload));
      sessionStorage.setItem("selectedPlan", JSON.stringify(payload));
    } catch (error) {
      console.error("Não foi possível salvar o plano na sessão:", error);
    }

    console.log(`Plano selecionado: ${plan.fullName}`, payload);
    navigate("/termos", { state: payload });
  };

  return (
    <>
      <Helmet>
        <title>PsyAssist Pro | A rotina ao redor do atendimento, organizada</title>
        <meta name="description" content="Para psicólogos que atendem por conta própria: agenda, confirmações, cobranças, prontuário e financeiro funcionando juntos. 7 dias grátis." />
        <meta name="keywords" content="PsyAssist Pro, sistema para psicólogos, prontuário psicológico, agenda para psicólogos, cobrança de sessões, confirmação de sessão, psicólogo autônomo" />
        <meta property="og:title" content="PsyAssist Pro | Você atende. O PsyAssist cuida da rotina ao redor." />
        <meta property="og:description" content="Agenda, confirmações, cobranças, prontuário e financeiro funcionando juntos para o psicólogo que atende por conta própria." />
      </Helmet>

      <section id="planos" className="lp-plans" aria-labelledby="plans-title">
        <div className="lp-shell">
          <header className="lp-plans__header">
            <div>
              <p className="lp-plans__eyebrow">Uma escolha para o seu momento</p>
              <h2 id="plans-title">Organizar, ou também automatizar.</h2>
            </div>
            <p>
              Dois planos pensados para quem atende de forma independente. No
              Essencial, você organiza e centraliza a rotina. No Premium, parte
              dela passa a acontecer sozinha.
            </p>
          </header>

          <div className="lp-plans__sheet">
            {PLANS.map((plan) => <PlanColumn key={plan.id} plan={plan} onSelect={selectPlan} />)}
          </div>

          <div className="lp-plans__notes">
            <p className="lp-plans__trial">
              <strong>7 dias grátis em qualquer plano.</strong> O cartão é
              solicitado no cadastro, mas a cobrança começa somente após o
              período de teste. A assinatura é mensal e não tem fidelidade.
            </p>
            <div className="lp-plans__choose">
              <h3>Como escolher</h3>
              <p>
                Se o que você precisa agora é tirar a rotina do papel e das
                planilhas, o Essencial resolve. Se confirmar sessões e cobrar
                pacientes é o que mais toma seu tempo, o Premium faz isso por você.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Plan;
